import {
  BookOpen,
  CalendarDays,
  ChartBar,
  Church,
  HandHeart,
  Home,
  MessageSquare,
  Mic,
  NotebookPen,
  Package,
  QrCode,
  Sparkles,
  UserCheck,
  Users,
} from "lucide-react";

// role: "pastor" | "coordinator" | "all"
export const GUIDES = [
  // ─── PASTOR ──────────────────────────────────────────────────────────
  {
    id:          "district-setup",
    role:        "pastor",
    icon:        Church,
    iconBg:      "bg-blue-50 dark:bg-blue-900/20",
    iconColor:   "text-blue-600 dark:text-blue-400",
    title:       "Tu distrito e iglesias",
    description: "Configura tu distrito y agrega tus congregaciones",
    faqs: [
      {
        q: "¿Cómo configuro mi distrito por primera vez?",
        a: "Al iniciar sesión, Kéves te guía para crear tu distrito. Ingresa el nombre del distrito, el campo misionero y tu información de contacto. Esto solo se hace una vez.",
      },
      {
        q: "¿Cómo agrego una iglesia?",
        a: "Ve a Iglesias en la barra de navegación → toca el botón +. Ingresa el nombre de la congregación, dirección, horarios de culto y el nombre del coordinador si ya lo tienes. Repite este paso para cada iglesia de tu distrito.",
      },
      {
        q: "¿Cómo registro los días de predicación de cada iglesia?",
        a: "Desde el detalle de la iglesia → toca el ícono de lápiz → sección 'Días de predicación'. Selecciona los días de la semana (sábado, miércoles, viernes, etc.) y el horario. Esto se usa para generar el rol de predicación.",
      },
      {
        q: "¿Cómo agrego familias y personas a una iglesia?",
        a: "Desde el detalle de la iglesia → toca 'Familias'. Desde Familias → toca + para crear un hogar nuevo. Luego, desde el detalle de la familia, agrega cada persona con su nombre, rol en el hogar y estado espiritual.",
      },
      {
        q: "¿Puedo cambiar el nombre o la información de mi distrito?",
        a: "Sí. Ve a Ajustes → toca tu nombre o el nombre del distrito en la parte superior → edita los campos que necesites. Los cambios se aplican de inmediato.",
      },
    ],
  },
  {
    id:          "member-portal",
    role:        "pastor",
    icon:        QrCode,
    iconBg:      "bg-teal-50 dark:bg-teal-900/20",
    iconColor:   "text-teal-600 dark:text-teal-400",
    title:       "Portal de miembros",
    description: "Activa el portal y comparte el acceso con tu congregación",
    faqs: [
      {
        q: "¿Qué es el portal de miembros?",
        a: "Es una app para los miembros de tu iglesia donde pueden ver eventos, anuncios, Biblia, devocional EGW, Escuela Sabática, himnario, 28 Creencias y Manual de Iglesia. Es gratuito para todos los miembros.",
      },
      {
        q: "¿Cómo accede un miembro al portal?",
        a: "Desde cualquier teléfono, entran a kevesapp.com → tocan 'Soy miembro' → 'Entrar con código'. Ingresan el código de acceso de su iglesia y ya están dentro.",
      },
      {
        q: "¿Dónde encuentro el código de acceso de mi iglesia?",
        a: "Ve al detalle de la iglesia → busca la sección 'Código de acceso' cerca de la parte superior. Es un código corto de 6 caracteres. Compártelo con tu congregación en el culto o por WhatsApp.",
      },
      {
        q: "¿Qué ve el miembro en el portal?",
        a: "El portal tiene 5 secciones: Inicio (resumen del día), Iglesia (anuncios, eventos, predicaciones, ministerios), Espiritual (Biblia, Devocional EGW, Escuela Sabática, himnario, 28 Creencias, Manual de Iglesia), Vida (versículos guardados) y Comunidad (mural de oración, cumpleaños).",
      },
      {
        q: "¿Los miembros pueden ver mis datos pastorales?",
        a: "No. El portal del miembro está completamente separado del sistema pastoral. Los miembros no pueden ver visitas, notas, estados espirituales ni ningún dato de gestión interna.",
      },
    ],
  },
  {
    id:          "coordinators",
    role:        "pastor",
    icon:        UserCheck,
    iconBg:      "bg-amber-50 dark:bg-amber-900/20",
    iconColor:   "text-amber-600 dark:text-amber-400",
    title:       "Coordinadores de iglesia",
    description: "Invita y gestiona a los líderes de cada congregación",
    faqs: [
      {
        q: "¿Qué puede hacer un coordinador en Kéves?",
        a: "El coordinador puede registrar visitas, ver el plan de predicaciones de su iglesia, consultar la agenda y ver eventos y anuncios. No tiene acceso a los datos de otras iglesias del distrito.",
      },
      {
        q: "¿Cómo invito a un coordinador?",
        a: "Ve al detalle de la iglesia → sección 'Coordinador' → toca 'Invitar coordinador'. Ingresa el correo electrónico del coordinador y envía la invitación. Recibirá un correo con el enlace para crear su cuenta.",
      },
      {
        q: "¿Cuántos coordinadores puede tener una iglesia?",
        a: "Cada iglesia puede tener un coordinador principal. Si necesitas agregar otro líder, primero debes reasignar o remover al actual desde el detalle de la iglesia.",
      },
      {
        q: "¿Cómo sé qué acciones hizo el coordinador?",
        a: "Las visitas registradas por el coordinador aparecen en el historial de la iglesia y en el módulo de Visitas de tu panel. También puedes ver la actividad reciente desde el detalle de la iglesia.",
      },
      {
        q: "¿Puedo remover a un coordinador?",
        a: "Sí. Desde el detalle de la iglesia → sección 'Coordinador' → toca 'Remover coordinador'. El acceso del coordinador se desactiva de inmediato.",
      },
    ],
  },
  {
    id:          "families",
    role:        "all",
    icon:        Users,
    iconBg:      "bg-emerald-50 dark:bg-emerald-900/20",
    iconColor:   "text-emerald-600 dark:text-emerald-400",
    title:       "Familias y Personas",
    description: "Registra y organiza los hogares de tu distrito",
    faqs: [
      {
        q: "¿Cómo registro una familia nueva?",
        a: "Ve a Familias → toca el botón + en la esquina superior derecha. Ingresa el nombre del hogar y selecciona la iglesia. Desde el detalle de la familia puedes agregar personas, registrar visitas y oraciones.",
      },
      {
        q: "¿Cómo agrego una persona a un hogar existente?",
        a: "Abre el detalle de la familia → toca 'Agregar persona'. Ingresa el nombre, rol en el hogar (padre/madre, hijo/a, etc.), fecha de nacimiento y estado espiritual.",
      },
      {
        q: "¿Qué significan los estados espirituales?",
        a: "Frecuente: asiste regularmente. No frecuente: asiste con poca frecuencia. Interesado: no es miembro pero muestra interés. A rescatar: miembro alejado que necesita atención pastoral.",
      },
      {
        q: "¿Cómo traslado una persona a otra familia o iglesia?",
        a: "Desde el detalle de la persona → toca 'Mover persona'. Selecciona la nueva familia. Si el traslado es a otra iglesia, selecciona primero la iglesia de destino.",
      },
      {
        q: "¿Cómo busco una persona específica?",
        a: "En la pantalla de Familias, usa la barra de búsqueda. Puedes buscar por nombre de persona, nombre de familia o iglesia. La búsqueda es en tiempo real.",
      },
    ],
  },
  {
    id:          "visits",
    role:        "all",
    icon:        HandHeart,
    iconBg:      "bg-violet-50 dark:bg-violet-900/20",
    iconColor:   "text-violet-600 dark:text-violet-400",
    title:       "Visitas",
    description: "Registra y planifica el seguimiento pastoral",
    faqs: [
      {
        q: "¿Cómo registro una visita realizada?",
        a: "Ve a Visitas → toca el botón +. Selecciona la persona o familia visitada, el tipo de visita, la fecha y agrega notas opcionales. Marca el estado como 'Realizada' si ya se hizo.",
      },
      {
        q: "¿Qué tipos de visita existen?",
        a: "General (visita de seguimiento), Hospitalaria (enfermedad o accidente), Luto (fallecimiento), Matrimonial (consejería de pareja), Evangelismo (persona no miembro), Urgente (crisis o necesidad inmediata).",
      },
      {
        q: "¿Cómo programo una visita futura?",
        a: "Al crear la visita, selecciona una fecha futura y estado 'Pendiente'. Aparecerá en el calendario como visita programada. También puedes reagendarla desde su detalle.",
      },
      {
        q: "¿Qué significa 'Atrasada'?",
        a: "Una visita programada cuya fecha ya pasó y no fue marcada como realizada. El sistema la destaca en rojo en el calendario. Desde el detalle puedes marcarla como realizada o reagendarla.",
      },
      {
        q: "¿Cómo veo el historial de visitas de una familia?",
        a: "Desde el detalle de la familia, desplázate hasta la sección 'Historial de visitas'. También puedes ver el historial de una persona desde su perfil.",
      },
    ],
  },
  {
    id:          "prayers",
    role:        "pastor",
    icon:        MessageSquare,
    iconBg:      "bg-rose-50 dark:bg-rose-900/20",
    iconColor:   "text-rose-600 dark:text-rose-400",
    title:       "Oraciones",
    description: "Lleva el muro de oración de tu congregación",
    faqs: [
      {
        q: "¿Cómo registro una petición de oración?",
        a: "Ve a Oraciones → toca el botón +. Escribe la petición, selecciona la persona o familia si aplica, y elige la categoría (salud, familia, trabajo, etc.). Las oraciones activas aparecen en el muro.",
      },
      {
        q: "¿Cómo marco una oración como respondida?",
        a: "Abre la oración desde la lista → toca 'Marcar como respondida'. Puedes agregar una nota de respuesta para recordar cómo Dios intervino. La oración pasa al historial de respondidas.",
      },
      {
        q: "¿Qué significan los estados de oración?",
        a: "Activa: petición en curso. Respondida: Dios respondió. Todas: incluye el historial completo. Las urgentes aparecen destacadas en la parte superior del muro.",
      },
      {
        q: "¿Cómo filtro oraciones por tipo?",
        a: "Usa los tabs en la pantalla principal: Activas, Respondidas, Todas. También puedes buscar por nombre de persona o contenido de la oración usando la barra de búsqueda.",
      },
      {
        q: "¿Puedo vincular una oración a una visita?",
        a: "Sí. Al registrar una visita puedes mencionar peticiones de oración en las notas. Desde el perfil de una persona o familia puedes ver tanto sus visitas como sus oraciones registradas.",
      },
    ],
  },
  {
    id:          "notes",
    role:        "pastor",
    icon:        NotebookPen,
    iconBg:      "bg-slate-100 dark:bg-slate-700",
    iconColor:   "text-slate-600 dark:text-slate-400",
    title:       "Notas",
    description: "Memoria pastoral y anotaciones del ministerio",
    faqs: [
      {
        q: "¿Para qué sirven las notas pastorales?",
        a: "Son tu cuaderno de memorias ministeriales. Puedes vincularlas a personas, familias, iglesias o eventos. Sirven para registrar conversaciones importantes, decisiones, observaciones espirituales y reflexiones.",
      },
      {
        q: "¿Cómo creo una nota vinculada a una persona?",
        a: "Ve a Notas → toca el botón +. Selecciona el tipo de enlace 'Persona' y busca el nombre. La nota quedará asociada y podrás verla desde el perfil de esa persona.",
      },
      {
        q: "¿Cuál es la diferencia entre notas 'local' y 'campo'?",
        a: "Local: visible solo para ti y el equipo de tu iglesia. Campo: visible para el equipo distrital. Úsala para compartir información relevante con otros pastores del distrito.",
      },
      {
        q: "¿Cómo busco en mis notas?",
        a: "En la pantalla de Notas, usa la barra de búsqueda. Busca por título, contenido o etiquetas. Las notas también se agrupan por recientes, esta semana y anteriores.",
      },
      {
        q: "¿Las notas se sincronizan aunque esté sin conexión?",
        a: "Sí. Las notas se guardan primero en el dispositivo y se sincronizan cuando hay conexión. Verás un indicador de 'pendiente de sync' hasta que se completen.",
      },
    ],
  },
  {
    id:          "agenda",
    role:        "all",
    icon:        CalendarDays,
    iconBg:      "bg-blue-50 dark:bg-blue-900/20",
    iconColor:   "text-blue-600 dark:text-blue-400",
    title:       "Agenda",
    description: "Gestiona eventos personales, locales y del campo",
    faqs: [
      {
        q: "¿Cuál es la diferencia entre los tres tipos de agenda?",
        a: "Personal: solo visible para ti. Local: eventos de tu iglesia, visible para el coordinador. Campo: eventos del distrito o campo misionero, visible para todo el equipo distrital.",
      },
      {
        q: "¿Cómo creo un evento recurrente?",
        a: "Al crear el evento, selecciona la fecha de inicio y una fecha de fin opcional si el evento dura varios días. Los eventos del culto de cada semana se crean individualmente con el mismo proceso.",
      },
      {
        q: "¿Cómo copio eventos del campo a mi agenda local?",
        a: "En la pestaña de tu iglesia → toca 'Copiar de campo'. Se listarán los eventos del campo que aún no están en tu agenda local. Selecciona los que deseas copiar.",
      },
      {
        q: "¿Cómo comparto un evento con mi congregación?",
        a: "Los eventos de tipo 'Local' son visibles para el coordinador de tu iglesia y aparecen en el portal del miembro como eventos de la semana.",
      },
      {
        q: "¿Puedo agregar hora y descripción a un evento?",
        a: "Sí. En el formulario de nuevo evento encontrarás campos para: título, fecha de inicio, hora, fecha de fin (opcional) y notas adicionales. El tipo de evento define si es pastoral, culto, reunión, etc.",
      },
    ],
  },
  {
    id:          "preaching",
    role:        "pastor",
    icon:        Mic,
    iconBg:      "bg-purple-50 dark:bg-purple-900/20",
    iconColor:   "text-purple-600 dark:text-purple-400",
    title:       "Predicaciones",
    description: "Planifica y asigna el rol de predicación",
    faqs: [
      {
        q: "¿Qué es un 'Rol de predicación'?",
        a: "Es el planificador de quién predica en cada culto durante un período (trimestral, semestral, etc.). Incluye las iglesias del distrito y permite asignar predicadores a cada fecha.",
      },
      {
        q: "¿Cómo creo un nuevo planificador de predicación?",
        a: "Ve a Predicaciones → toca el botón +. Selecciona el tipo de período (trimestral, semestral, etc.), el año, el mes de inicio y las iglesias a incluir. El sistema crea automáticamente la estructura.",
      },
      {
        q: "¿Cómo agrego fechas de predicación?",
        a: "En el planificador de una iglesia, usa los botones 'Sábados', 'Miércoles', 'Viernes' para agregar todas las fechas de ese día en el período. O toca un día del calendario para agregar una fecha específica.",
      },
      {
        q: "¿Cómo asigno un predicador?",
        a: "En el planner, toca la fecha → selecciona 'Asignar'. Aparece un buscador con los miembros elegibles (bautizados, frecuentes o no frecuentes). También puedes escribir un nombre externo en 'Predicador externo'.",
      },
      {
        q: "¿Qué significa 'Conflicto' en una fecha?",
        a: "Significa que el mismo predicador está asignado en dos iglesias el mismo día. Revisa las fechas en rojo, reasigna uno de los predicadores para resolver el conflicto antes de publicar el rol.",
      },
    ],
  },
  {
    id:          "baptisms",
    role:        "pastor",
    icon:        BookOpen,
    iconBg:      "bg-cyan-50 dark:bg-cyan-900/20",
    iconColor:   "text-cyan-600 dark:text-cyan-400",
    title:       "Bautismos y Estudios Bíblicos",
    description: "Seguimiento de candidatos al bautismo",
    faqs: [
      {
        q: "¿Cómo veo quiénes son candidatos al bautismo?",
        a: "Ve a Evangelismo → Bautismos. La lista muestra personas con estado 'En oración', 'En estudio' o 'Listo'. Los candidatos son mayores de 7 años no bautizados ni rebautizados.",
      },
      {
        q: "¿Cómo inicio un estudio bíblico con un candidato?",
        a: "Desde la lista de bautismos, toca la persona → toca 'Estudio bíblico'. Ingresa el nombre del curso, total de lecciones, instructor y fecha de inicio. Puedes registrar el progreso lección por lección.",
      },
      {
        q: "¿Cómo registro el bautismo de una persona?",
        a: "Desde el detalle del candidato en la lista de bautismos → toca 'Registrar bautismo'. Ingresa la fecha y notas opcionales. El estado de la persona cambia automáticamente a 'Bautizado'.",
      },
      {
        q: "¿Qué significa 'Listo' en el estado de un candidato?",
        a: "'De blanco' o 'Listo' significa que el candidato completó su preparación y está listo para bautizarse. Puedes cambiar a este estado desde el seguimiento del estudio bíblico.",
      },
      {
        q: "¿Cómo veo todos los estudios bíblicos del distrito?",
        a: "Ve a Evangelismo → Estudios bíblicos. Muestra todos los estudios activos del distrito con progreso, instructor y estado. Desde ahí puedes navegar al detalle de cada estudio.",
      },
    ],
  },
  {
    id:          "groups",
    role:        "pastor",
    icon:        Home,
    iconBg:      "bg-emerald-50 dark:bg-emerald-900/20",
    iconColor:   "text-emerald-600 dark:text-emerald-400",
    title:       "Grupos Pequeños",
    description: "Células y reuniones de hogar del distrito",
    faqs: [
      {
        q: "¿Cómo creo un grupo pequeño?",
        a: "Ve a Evangelismo → Grupos pequeños → toca el botón +. Ingresa el nombre del grupo, eslogan opcional, líder, iglesia, dirección y horario de reunión. Guarda para crearlo.",
      },
      {
        q: "¿Cómo agrego miembros a un grupo?",
        a: "Desde el detalle del grupo → toca 'Agregar' en la sección de miembros. Busca la persona por nombre. Si no aparece en el sistema, toca 'Crear nuevo hogar' para registrarla.",
      },
      {
        q: "¿Puedo asignar un líder que no sea miembro registrado?",
        a: "El buscador de líderes muestra personas bautizadas y frecuentes. Si el líder no aparece, asegúrate de que esté registrado como persona en el sistema con estado espiritual correcto.",
      },
      {
        q: "¿Cómo edito la información de un grupo?",
        a: "Desde el detalle del grupo → toca el ícono de lápiz en la esquina superior derecha. Puedes actualizar nombre, horario, líder, dirección y notas.",
      },
      {
        q: "¿Cómo elimino un grupo?",
        a: "Desde el detalle del grupo → toca el ícono de basurero → confirma la eliminación. Esto elimina el grupo y sus registros de membresía. Los perfiles de las personas no se ven afectados.",
      },
    ],
  },
  {
    id:          "materials",
    role:        "pastor",
    icon:        Package,
    iconBg:      "bg-stone-100 dark:bg-stone-800/60",
    iconColor:   "text-stone-600 dark:text-stone-400",
    title:       "Materiales",
    description: "Inventario y distribución de recursos",
    faqs: [
      {
        q: "¿Cómo registro una distribución de materiales a una iglesia?",
        a: "Ve a Materiales → Distribuciones → toca +. Selecciona la iglesia, el material, la cantidad y el precio unitario. Guarda para crear la distribución en estado 'Pendiente de entrega'.",
      },
      {
        q: "¿Qué diferencia hay entre 'Pendiente', 'Entregado' y 'Pagado'?",
        a: "Pendiente: la distribución fue registrada pero el material aún no se entregó. Entregado: los materiales llegaron a la iglesia (descuenta del inventario). Pagado: la iglesia liquidó su deuda.",
      },
      {
        q: "¿Cómo marco una distribución como entregada?",
        a: "Desde el detalle de la distribución → toca 'Marcar como entregado'. Confirma la entrega. Esto descuenta automáticamente las unidades del inventario global.",
      },
      {
        q: "¿Cómo registro un pago de una iglesia?",
        a: "Desde el detalle de la distribución o desde la cuenta de la iglesia → toca 'Registrar pago'. Ingresa el monto, fecha, método de pago y si fue al distrito o directamente a la asociación.",
      },
      {
        q: "¿Qué es el 'Stock propio' de materiales?",
        a: "Son materiales que el distrito o iglesia adquirió independientemente (no asignados por la asociación). Se registra en el módulo de stock propio para llevar control separado.",
      },
    ],
  },
  {
    id:          "reports",
    role:        "pastor",
    icon:        ChartBar,
    iconBg:      "bg-indigo-50 dark:bg-indigo-900/20",
    iconColor:   "text-indigo-600 dark:text-indigo-400",
    title:       "Reportes y estadísticas",
    description: "Entiende la salud de tu distrito con un vistazo",
    faqs: [
      {
        q: "¿Qué información muestran los reportes?",
        a: "Los reportes ofrecen indicadores de salud pastoral: total de familias e iglesias, visitas realizadas vs. pendientes, candidatos al bautismo, oraciones activas y comparativas por período.",
      },
      {
        q: "¿Cómo accedo a los reportes del distrito?",
        a: "Ve a la barra de navegación → Reportes (ícono de gráfica). Puedes filtrar por iglesia, fecha y categoría. El informe descargable incluye todos los módulos del distrito.",
      },
      {
        q: "¿Cómo veo el reporte de una iglesia específica?",
        a: "Desde el detalle de la iglesia → toca 'Reportes'. Muestra el estado de esa congregación: familias, visitas recientes, estado de materiales y cumpleaños próximos.",
      },
      {
        q: "¿Puedo exportar los datos del distrito?",
        a: "Sí. Desde Reportes → toca 'Exportar'. Genera un archivo Excel con todos los datos del distrito: familias, personas, visitas, bautismos y materiales. Útil para informes a la asociación.",
      },
      {
        q: "¿Cada cuánto se actualizan los datos?",
        a: "Los datos se sincronizan en tiempo real cada vez que hay conexión a internet. Puedes forzar la actualización deslizando hacia abajo desde cualquier pantalla de lista.",
      },
    ],
  },
  {
    id:          "kevia",
    role:        "pastor",
    icon:        Sparkles,
    iconBg:      "bg-indigo-50 dark:bg-indigo-900/20",
    iconColor:   "text-indigo-600 dark:text-indigo-400",
    title:       "Kevia IA",
    description: "Asistente pastoral inteligente de Kéves",
    faqs: [
      {
        q: "¿Qué es Kevia?",
        a: "Kevia es el asistente pastoral integrado en Kéves. Tiene acceso a los datos de tu distrito y puede responder preguntas, ayudarte a planificar, resumir el estado pastoral y sugerir acciones de seguimiento.",
      },
      {
        q: "¿Cómo accedo a Kevia?",
        a: "Toca el ícono de chispa en la barra de navegación inferior. Kevia está disponible en toda la app y tiene contexto de la pantalla que estás viendo (familia, persona, iglesia, etc.).",
      },
      {
        q: "¿Qué tipo de preguntas puedo hacerle a Kevia?",
        a: "Puedes preguntar: '¿Cuáles familias no he visitado este mes?', '¿Qué candidatos están cerca del bautismo?', '¿Cuántas oraciones activas hay?', 'Resume el estado de la Iglesia Central', entre muchas más.",
      },
      {
        q: "¿Kevia conoce la doctrina adventista?",
        a: "Sí. Kevia tiene acceso a las 28 Creencias Fundamentales, el Manual de Iglesia 2025, escritos de Elena de White y guías ministeriales adventistas. Puede orientarte en preguntas doctrinales y de procedimiento.",
      },
      {
        q: "¿Los datos que comparto con Kevia son privados?",
        a: "Sí. Kevia solo accede a los datos de tu distrito. Las conversaciones no se comparten con otros pastores ni con terceros. Solo tú y Kevia tienen acceso al contenido de tus chats.",
      },
    ],
  },

  // ─── COORDINADOR ─────────────────────────────────────────────────────
  {
    id:          "coordinator-start",
    role:        "coordinator",
    icon:        UserCheck,
    iconBg:      "bg-blue-50 dark:bg-blue-900/20",
    iconColor:   "text-blue-600 dark:text-blue-400",
    title:       "Tus primeros pasos",
    description: "Bienvenido a Kéves. Esto es lo esencial para empezar",
    faqs: [
      {
        q: "¿Qué puede hacer un coordinador en Kéves?",
        a: "Como coordinador de iglesia, puedes registrar visitas, ver el plan de predicaciones, consultar la agenda de la iglesia y ver anuncios. Es una herramienta para que el pastor y tú trabajen juntos en el cuidado de la congregación.",
      },
      {
        q: "¿Cómo inicio sesión por primera vez?",
        a: "El pastor te envió una invitación por correo. Abre ese correo, toca el enlace de activación y crea tu contraseña. A partir de ahí puedes entrar desde kevesapp.com con tu correo y contraseña.",
      },
      {
        q: "¿Puedo usar Kéves desde mi celular?",
        a: "Sí. Kéves funciona en cualquier teléfono desde el navegador. Para una mejor experiencia, agrega la app a tu pantalla de inicio: en Safari (iPhone) o Chrome (Android) toca 'Compartir' → 'Agregar a inicio'.",
      },
      {
        q: "¿Qué pasa si no tengo internet?",
        a: "Kéves funciona sin conexión. Puedes registrar visitas y ver datos guardados aunque no haya señal. Cuando recuperes internet, todo se sincroniza automáticamente.",
      },
      {
        q: "¿Cómo contacto al pastor si tengo un problema?",
        a: "Desde Ajustes → toca 'Enviar mensaje al pastor'. También puedes usar el módulo de Visitas para dejar notas en los registros que el pastor verá desde su panel.",
      },
    ],
  },
  {
    id:          "coordinator-visits",
    role:        "coordinator",
    icon:        HandHeart,
    iconBg:      "bg-violet-50 dark:bg-violet-900/20",
    iconColor:   "text-violet-600 dark:text-violet-400",
    title:       "Registrar visitas",
    description: "Cómo llevar el seguimiento pastoral de tu iglesia",
    faqs: [
      {
        q: "¿Cómo registro una visita que hice?",
        a: "Ve a Visitas desde tu panel de coordinador → toca el botón + en la esquina. Selecciona la familia o persona que visitaste, escoge el tipo de visita, agrega la fecha y una nota breve de lo que conversaron.",
      },
      {
        q: "¿Qué tipos de visita puedo registrar?",
        a: "General (seguimiento regular), Hospitalaria (por enfermedad), Luto (acompañamiento en pérdida), Matrimonial (consejería de pareja), Evangelismo (contacto no miembro), Urgente (crisis o necesidad inmediata).",
      },
      {
        q: "¿El pastor puede ver las visitas que yo registro?",
        a: "Sí. Todo lo que registras es visible para el pastor. Es importante porque le ayuda a coordinar el cuidado del rebaño sin duplicar esfuerzos.",
      },
      {
        q: "¿Puedo planificar visitas para días siguientes?",
        a: "Sí. Al crear la visita, selecciona una fecha futura y deja el estado en 'Pendiente'. Aparecerá en tu lista de pendientes y en el calendario del pastor.",
      },
      {
        q: "¿Qué pongo en las notas de una visita?",
        a: "Las notas son confidenciales (solo tú y el pastor las ven). Puedes escribir el motivo de la visita, cómo estaba la familia espiritualmente, peticiones de oración o cualquier seguimiento necesario.",
      },
    ],
  },
  {
    id:          "coordinator-preaching",
    role:        "coordinator",
    icon:        Mic,
    iconBg:      "bg-purple-50 dark:bg-purple-900/20",
    iconColor:   "text-purple-600 dark:text-purple-400",
    title:       "Predicaciones y Escuela Sabática",
    description: "Consulta el plan de predicaciones y la lección del sábado",
    faqs: [
      {
        q: "¿Cómo veo quién predica cada sábado en mi iglesia?",
        a: "Desde el panel de coordinador, en la sección de Predicaciones, puedes ver el rol del trimestre actual con todas las fechas y los predicadores asignados. El pastor actualiza este plan.",
      },
      {
        q: "¿Cómo accedo a la lección de la Escuela Sabática?",
        a: "El portal del miembro incluye la lección del día en la sección Espiritual → Escuela Sabática. Si tú mismo tienes acceso al portal, puedes encontrarla ahí también.",
      },
      {
        q: "¿Puedo ver eventos del campo y del distrito?",
        a: "Sí. En la sección de Agenda del coordinador puedes ver los eventos de tu iglesia y los eventos del campo (campo misionero o unión) que el pastor haya registrado.",
      },
      {
        q: "¿Qué hago si hay un error en el rol de predicación?",
        a: "Comunícaselo al pastor. Solo él puede modificar el rol de predicaciones. Puedes enviarle un mensaje desde Ajustes → 'Enviar mensaje al pastor'.",
      },
    ],
  },
  {
    id:          "coordinator-communication",
    role:        "coordinator",
    icon:        MessageSquare,
    iconBg:      "bg-teal-50 dark:bg-teal-900/20",
    iconColor:   "text-teal-600 dark:text-teal-400",
    title:       "Comunicación con el pastor",
    description: "Mantente conectado con tu pastor de distrito",
    faqs: [
      {
        q: "¿Cómo le envío un mensaje al pastor?",
        a: "Ve a Ajustes → 'Enviar mensaje al pastor'. Escribe tu mensaje y envíalo. El pastor lo recibirá en su panel y podrá responderte.",
      },
      {
        q: "¿Cómo reporto un problema técnico?",
        a: "Ve a Ajustes → 'Algo no funciona'. Describe qué ocurrió y desde qué pantalla. El reporte llega directamente al equipo de soporte de Kéves.",
      },
      {
        q: "¿Cómo veo los anuncios que el pastor publicó?",
        a: "Los anuncios aparecen en la sección de tu iglesia (desde el panel principal). También puedes verlos en el portal del miembro si tienes acceso.",
      },
      {
        q: "¿Cómo sé si el pastor revisó mis registros?",
        a: "Kéves no tiene notificaciones de 'leído'. Lo que sí puedes hacer es verificar que tus visitas y notas aparezcan correctamente guardadas con el ícono de sincronización en verde.",
      },
    ],
  },
];
